import React, { useMemo } from "react";
import { AlertOctagon } from "lucide-react";

/**
 * Postmenopausal bleeding red-flag alert.
 * Flashes at the top of a patient chart when BOTH conditions are true:
 *   1. Patient is postmenopausal (explicit flag OR age >= 55 with sex=Female,
 *      OR age >= 50 with sex=Female and any "menopause" keyword in known/history).
 *   2. Any bleeding-related keyword appears in chief complaints, diagnosis,
 *      known conditions, or the selected acute condition label.
 *
 * Endometrial cancer risk after 1 episode of postmenopausal bleeding is
 * ~9-10 % — endometrial biopsy is mandatory. This banner exists so that
 * signal is never missed on a busy OP day.
 */
const BLEEDING_KEYWORDS = [
  "bleed", "bleeding", "spotting", "postmenopausal bleed", "pmb",
  "vaginal bleeding", "uterine bleeding", "brown discharge",
  "रक्तस्राव", "रक्तस्त्राव", "रक्त",
];

function textIncludesAny(text, keywords) {
  if (!text) return false;
  const t = String(text).toLowerCase();
  return keywords.some((k) => t.includes(k.toLowerCase()));
}

export default function PostmenopausalBleedingBanner({ patient, chief, known, diagnosis, acuteCondition }) {
  const isMenopausal = useMemo(() => {
    if (!patient || (patient.sex || "").toLowerCase().startsWith("m")) return false;
    if (patient.menopausal === true) return true;
    const knownStr = Array.isArray(known) ? known.join(" ") : String(known || "");
    const menopauseInKnown = /menopause|postmenopaus|climacteric|रजोनिवृ/i.test(knownStr);
    const age = Number(patient.age || 0);
    // Explicit flag > 55 (default assumed menopausal) > 50 with keyword.
    return (age >= 55) || (age >= 50 && menopauseInKnown);
  }, [patient, known]);

  const hasBleeding = useMemo(() => {
    if (textIncludesAny(chief, BLEEDING_KEYWORDS)) return true;
    if (textIncludesAny(diagnosis?.primary, BLEEDING_KEYWORDS)) return true;
    if (textIncludesAny(diagnosis?.secondary, BLEEDING_KEYWORDS)) return true;
    if (textIncludesAny(diagnosis?.notes, BLEEDING_KEYWORDS)) return true;
    if (textIncludesAny(acuteCondition?.label, BLEEDING_KEYWORDS)) return true;
    return false;
  }, [chief, diagnosis, acuteCondition]);

  if (!isMenopausal || !hasBleeding) return null;

  return (
    <div
      role="alert"
      className="border-2 border-rose-600 bg-rose-50 rounded-lg px-3 py-2.5 mb-3 flex items-start gap-3 animate-pulse-slow"
      data-testid="pmb-red-flag-banner"
    >
      <div className="mt-0.5 rounded-full bg-rose-600 p-1.5 text-white shrink-0">
        <AlertOctagon size={16} />
      </div>
      <div className="flex-1 text-[12px] leading-snug">
        <div className="font-bold text-rose-800 uppercase tracking-wider text-[11px]">
          🚨 Postmenopausal Bleeding — Red Flag
        </div>
        <div className="text-rose-800 mt-0.5">
          Any bleeding in a postmenopausal woman is <strong>endometrial cancer until proven otherwise</strong> (baseline risk ≈ 9-10%).
          <br />
          <strong>Endometrial biopsy is mandatory.</strong> Also do TVS (endometrial thickness &lt; 4 mm has very high NPV), Pap smear, and CA-125 if indicated.
        </div>
      </div>
    </div>
  );
}
